// Profilo sintetico AI: dati di ingresso, conteggio delle informazioni utili e firma delle sigle.
// La generazione è lato server (contratto tappa 6): qui solo ciò che il frontend prepara e confronta.

import moment from "moment";

import {
	ATTITUDES,
	playerDepartment,
	statureFromHeight,
} from "@/utils/scoutingData";
import {per90, seasonRows} from "@/utils/wyscoutImport";

const DEPARTMENT_LABELS = {
	P: "portiere",
	D: "difesa",
	C: "centrocampo",
	A: "attacco",
};

const attitudeFrequency = (visions) => {
	const counts = {};
	(visions || []).forEach((v) =>
		(v.attitudes || []).forEach((a) => (counts[a] = (counts[a] || 0) + 1)),
	);
	return counts;
};

// Dati di ingresso, con le sole voci presenti.
export const buildProfileInputs = (
	player,
	{visions = [], matches = []} = {},
) => {
	const height = player?.height || player?.lastHeight || null;
	const lastSeason = seasonRows(matches, {officialOnly: true})[0];
	const t = lastSeason?.totals;
	const inputs = {
		age: player?.dateOfBirth
			? moment().diff(moment(player.dateOfBirth), "years")
			: null,
		department: DEPARTMENT_LABELS[playerDepartment(player)] || null,
		role: player?.internalRole?.code || player?.secondaryRole || null,
		alternativeRoles: (player?.alternativeRoles || []).map((r) => r.code || r),
		foot: player?.favoriteFoot || null,
		height: height ? Number(height) : null,
		stature: statureFromHeight(height),
		somatotype: player?.somatotype || null,
		proportions: player?.proportions || null,
		attitudes: player?.attitudes || [],
		visions: visions.length
			? {
					count: visions.length,
					starter: visions.filter((v) => v.starter === true).length,
					attitudes: attitudeFrequency(visions),
					lastNotes: visions
						.map((v) => v.note)
						.filter(Boolean)
						.slice(0, 3),
				}
			: null,
		notes: (player?.notes || "").trim() || null,
		stats:
			t && t.minutes
				? {
						season: lastSeason.season,
						matches: t.matches,
						minutes: t.minutes,
						goals90: per90(t.goals, t.minutes),
						xg90: per90(t.xg, t.minutes),
						assists90: per90(t.assists, t.minutes),
					}
				: null,
	};
	return Object.fromEntries(
		Object.entries(inputs).filter(([, v]) =>
			Array.isArray(v) ? v.length : v !== null && v !== undefined,
		),
	);
};

// Età e reparto da soli non bastano a descrivere un giocatore.
const DESCRIPTIVE = [
	"role",
	"alternativeRoles",
	"foot",
	"height",
	"somatotype",
	"proportions",
	"attitudes",
	"visions",
	"notes",
	"stats",
];

export const MIN_USEFUL_INPUTS = 2;

export const countUsefulInputs = (inputs) =>
	DESCRIPTIVE.filter((key) => inputs?.[key] !== undefined).length;

// Firma delle sigle: se cambia dopo la generazione, il profilo va rigenerato.
export const profileSignature = (inputs) =>
	[
		inputs?.department,
		inputs?.role,
		[...(inputs?.alternativeRoles || [])].join("+"),
		inputs?.stature,
		inputs?.somatotype,
		inputs?.proportions,
		ATTITUDES.map((a) => a.id)
			.filter((id) => (inputs?.attitudes || []).includes(id))
			.join("+"),
	]
		.map((v) => v || "-")
		.join("|");
